import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit, signal } from "@angular/core";
import { RouterLink } from "@angular/router";
import { Log } from "oidc-client-ts";
import { AuthService } from "../services/auth.service";
import { CodeBlock } from "../shared/code-block/code-block";

@Component({
  selector: "app-callback",
  imports: [RouterLink, CodeBlock],
  templateUrl: "./callback.html",
  styleUrls: ["./callback.scss"],
  host: { class: "mds-page mds-container" },
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Callback implements OnInit {
  readonly authService = inject(AuthService);

  protected readonly statusText = signal("Processing callback...");
  protected readonly statusState = signal("idle");
  protected readonly tokenPanelVisible = signal(false);
  protected readonly errorVisible = signal(false);
  protected readonly errorMessage = signal("");
  protected readonly idTokenRaw = signal("");
  protected readonly accessTokenRaw = signal("");
  protected readonly refreshTokenRaw = signal("");
  protected readonly accessTokenDecoded = signal("");
  protected readonly profileData = signal("");

  ngOnInit(): void {
    this.processCallback();
  }

  async signOut(): Promise<void> {
    try {
      await this.authService.logout();
    } catch {
      await this.authService.userManager.removeUser();
      window.location.href = "/";
    }
  }

  private async processCallback(): Promise<void> {
    Log.setLogger({
      error: (...args: unknown[]) => console.error("[oidc]", ...args),
      warn: (...args: unknown[]) => console.warn("[oidc]", ...args),
      info: (...args: unknown[]) => console.info("[oidc]", ...args),
      debug: (...args: unknown[]) => console.debug("[oidc]", ...args),
    });
    Log.setLevel(Log.INFO);

    const params = new URLSearchParams(window.location.search);

    if (params.has("error")) {
      const error = params.get("error");
      const desc = params.get("error_description") ?? "";
      this.setStatus(`Authorization error: ${error}`, "error");
      this.errorVisible.set(true);
      this.errorMessage.set(`error: ${error}\nerror_description: ${desc}`);
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }

    if (params.has("code")) {
      try {
        const user = await this.authService.userManager.signinRedirectCallback();
        window.history.replaceState({}, "", window.location.pathname);
        this.displayUser(user);
        return;
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : String(e);
        this.setStatus(`Callback failed: ${message}`, "error");
        this.errorVisible.set(true);
        this.errorMessage.set(message);
        window.history.replaceState({}, "", window.location.pathname);
        return;
      }
    }

    try {
      const user = await this.authService.getUser();
      if (user && !user.expired) {
        this.displayUser(user);
      } else {
        this.setStatus("No active session", "idle");
      }
    } catch {
      this.setStatus("No session", "idle");
    }
  }

  private setStatus(text: string, state: string): void {
    this.statusText.set(text);
    this.statusState.set(state);
  }

  private displayUser(user: { id_token?: string; access_token?: string; refresh_token?: string; profile?: Record<string, unknown> }): void {
    this.tokenPanelVisible.set(true);
    const identifier = (user.profile?.["email"] as string) ?? (user.profile?.["sub"] as string) ?? "unknown";
    this.setStatus(`Authenticated as ${identifier}`, "success");

    if (user.id_token) {
      this.idTokenRaw.set(user.id_token);
    }
    if (user.access_token) {
      this.accessTokenRaw.set(user.access_token);
      const decoded = this.decodeJwt(user.access_token);
      this.accessTokenDecoded.set(decoded ? JSON.stringify(decoded, null, 2) : "(opaque token - not a JWT)");
    }
    if (user.refresh_token) {
      this.refreshTokenRaw.set(user.refresh_token);
    }
    this.profileData.set(JSON.stringify(user.profile, null, 2));
  }

  private decodeJwt(token: string): { header: unknown; payload: unknown } | null {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) return null;
      const decode = (s: string) => JSON.parse(atob(s.replace(/-/g, "+").replace(/_/g, "/")));
      return { header: decode(parts[0]), payload: decode(parts[1]) };
    } catch {
      return null;
    }
  }
}